import React, { useEffect, useState } from 'react';
import CommonDetail from '../common/common-detail';
import CommonTableList from '../common/common-table-list';
import SeparateDetail from './separate-detail';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnsType } from 'antd/es/table';
import { api_separate } from '../../api/separate';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { separate, initSeparate } from '../../model/separate';
import SeparateDetailMulti from './separate-detail-multi';
import { printSeparateDocument } from '../../utils/record-print-utils';
import { Modal } from 'antd';
import { isEmpty } from '../../utils/util';
import { deleteFailureMessage } from '../../utils/write-result';

const Separate: React.FC = (prop) => {
    
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('');
    const [title, setTitle] = useState('');
    const [data, setData] = useState([]);
    const [separate, setSeparate] = useState<separate>(initSeparate);
    const [separateMulti, setSeparateMulti] = useState<separate[]>([initSeparate]);
    const [remark, setRemark] = useState('');
    const [saving, setSaving] = useState(false);
    // One key per *intended* single-item submission -- generated when the
    // ADD form is opened (see onShowDetail), not on every Save click. A
    // failed/retried Save on the same still-open form reuses this same key
    // (so the hardened Backend's idempotency actually protects against a
    // duplicate Stock movement on retry); opening the form again for a new
    // entry gets a fresh key.
    const [singleIdempotencyKey, setSingleIdempotencyKey] = useState('');
    // Incremented on every onShowDetail() call (ADD/VIEW/EDIT alike) and
    // used as SeparateDetail's React `key` below. This forces a hard
    // unmount+remount of SeparateDetail every time a NEW session is opened,
    // instead of relying on the Drawer's `destroyOnClose` CSS-animation-
    // timed unmount -- confirmed racy: opening View on a just-created row
    // fast enough after Save could reuse the still-mounted ADD instance,
    // whose mount-only init() never re-ran for the new document's id,
    // leaving the ADD form's leftover state on screen instead of the
    // Backend snapshot.
    const [openSeq, setOpenSeq] = useState(0);

    const filterField: any = ['create_date', 'from_product_code', 'from_product_name', 'to_product_code', 'to_product_name'];

    const placeholder_txt: string = 'วันที่ / รหัสสินค้าต้นทาง / สินค้าต้นทาง / รหัสสินค้าปลายทาง / สินค้าปลายทาง' 

    const onPrintSeparate = (record: any) => {
        printSeparateDocument(record);
    };

    const columns: ColumnsType<any> = [
        { title: 'วันที่', dataIndex: 'create_date', key: 'create_date', width: '200px', },
        { title: 'สินค้าต้นทาง', dataIndex: 'from_product_name', key: 'from_product_name' },
        { title: 'จำนวนสินค้าต้นทาง', dataIndex: 'from_quantity', key: 'from_quantity', render: (_, record) =>
            data.length >= 1 ? ( 
                <label>{record.from_quantity} {record.from_product_unit_name}</label>
            ) : null
        },
        { title: 'สินค้าปลายทาง', dataIndex: 'to_product_name', key: 'to_product_name' },
        { title: 'จำนวนสินค้าปลายทาง', dataIndex: 'to_quantity', key: 'to_quantity', render: (_, record) =>
            data.length >= 1 ? ( 
                <label>{record.to_quantity} {record.to_product_unit_name}</label>
            ) : null
        },
        { title: 'การดำเนินการ', dataIndex: 'operation', width: '130px', fixed: 'right', render: (_, record) =>
            data.length >= 1 ? ( 
                <div className='iconOperation'>
                    <FontAwesomeIcon icon="eye" title="ดูรายละเอียด" onClick={() => { onShowDetail('VIEW', record) }} />
                    <FontAwesomeIcon icon="print" title="พิมพ์ใบแยกแพค" onClick={() => { onPrintSeparate(record) }} />
                </div>
            ) : null
        }
    ]

    const init = async () => {
        setData([]);
        let getSeparate = await api_separate('LIST', {});
        setData(getSeparate);
    }

    const getConfigModal = (titile: string, content: string) => {
        return {
            title: titile,
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: content,
            okText: 'ตกลง',
            cancelText: 'ยกเลิก',
        }
    }

    const onShowDetail = (mode: string, separateData?: separate) => {
        setOpenSeq(seq => seq + 1);
        if (mode === 'MULTI') {
            setTitle('แยกแพคหลายรายการ')
            setSeparateMulti([]);
        } else {
        if (mode === 'ADD' || mode === 'COPY') {
            setTitle('แยกแพค')
            // A new intended single-item submission starts here -- one key
            // for this attempt, reused across retries of it (see onSave),
            // replaced only when the form is opened again.
            setSingleIdempotencyKey(`web-sp-${Date.now()}-${Math.floor(Math.random() * 1000000)}`);
            } else if (mode === 'EDIT') {
                setTitle('แก้ไขการแยกแพค')    
            } else if (mode === 'VIEW') {
                setTitle('รายละเอียดการแยกแพค')
            }
            setSeparate(separateData !== undefined && separateData !== null ? separateData : initSeparate);
        }

        setMode(mode);
        setOpen(true);
    };

    const onCloseDetail = () => {
        setOpen(false);
    };

    // Backend error body shape (see handler/transaction/separate/app.py):
    // {'error': 'INSUFFICIENT_STOCK' | 'SEPARATE_NOT_FOUND' | 'VALIDATION_ERROR', 'message': '...'}
    // on 409/404/400. A raw axios rejection (no `response` at all) means the
    // request never got a response -- network failure, timeout, CORS, etc.
    const getSaveErrorMessage = (err: any): string => {
        const status = err?.response?.status;
        const backendMessage = err?.response?.data?.message;

        if (status === 409) {
            return backendMessage || 'สต๊อกสินค้าไม่เพียงพอสำหรับการแยกแพ็ค';
        }
        if (status === 400) {
            return backendMessage || 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบข้อมูลที่กรอก';
        }
        if (status) {
            return backendMessage || 'บันทึกรายการไม่สำเร็จ กรุณาลองใหม่อีกครั้ง';
        }
        if (err?.request) {
            return 'บันทึกรายการไม่สำเร็จ กรุณาลองใหม่อีกครั้ง';
        }
        return 'เกิดข้อผิดพลาดที่ไม่คาดคิด กรุณาลองใหม่อีกครั้ง';
    }

    const onSave = async () => {
        // Defense in depth beyond the disabled Save button -- e.g. a fast
        // Enter-key resubmit landing between the click and the button
        // actually re-rendering as disabled must not fire a second request.
        if (saving) {
            return;
        }

        let operation = 'INSERT';
        let key_id = undefined;
        let body = JSON.parse(JSON.stringify(separate));

        if (mode == 'MULTI') {
            let isPass = true;
            separateMulti.forEach((object: any) => {
                if (isEmpty(object['from_product']) || isEmpty(object['to_product']) || isEmpty(object['from_quantity'])
                    || isEmpty(object['to_quantity']) || object['from_quantity'] <= 0 || object['to_quantity'] <= 0) {
                    isPass = false;
                    Modal.warning({
                        ...getConfigModal('แจ้งเตือน', 'กรุณากรอกข้อมูลให้ครบถ้วน')
                    });

                    return false;
                }
            });

            if (isPass) {
                operation = 'INSERT_MULTI';
                body = {
                    "separate": separateMulti,
                    "remark": remark,
                    // Backend derives a deterministic per-item idempotency key
                    // from this batch key (batchKey:index) -- retrying the
                    // same submission (e.g. after a timeout) returns the
                    // already-created documents instead of duplicating Stock
                    // movements.
                    "idempotency_key": `web-${Date.now()}-${Math.floor(Math.random() * 1000000)}`
                }

                setSaving(true);
                try {
                    let res = await api_separate(operation, body, key_id);
                    // insert_multi now returns the array of created/confirmed
                    // Separate documents (with their authoritative snapshot
                    // values), not a bare 1 -- see MDreport for the Phase 5
                    // bulk-hardening writeup.
                    if (Array.isArray(res) && res.length > 0) {
                        setOpen(false);
                        Modal.success({
                            ...getConfigModal('สำเร็จ', 'เพิ่มข้อมูลสำเร็จแล้ว'),
                            ...{ onOk() { init(); }}
                        });
                    } else {
                        Modal.error({
                            ...getConfigModal('ผิดพลาด', 'เพิ่มข้อมูลไม่สำเร็จ'),
                        });
                    }
                } catch (err) {
                    // api_separate() does not catch axios rejections itself
                    // (a non-2xx Backend response, e.g. 409 insufficient
                    // stock, or a network failure, rejects the promise) --
                    // this used to propagate as a silent unhandled rejection
                    // with zero UI feedback. Must always surface something.
                    Modal.error({
                        ...getConfigModal('ผิดพลาด', getSaveErrorMessage(err)),
                    });
                } finally {
                    setSaving(false);
                }
            }
        } else {
            if (separate.id > 0) {
                operation = 'UPDATE';
                key_id = separate.id;
            }

            if (!isEmpty(body['from_product']) && !isEmpty(body['from_quantity']) && !isEmpty(body['to_product']) && !isEmpty(body['to_quantity'])) {
                body['from_product'] = isEmpty(body['from_product']) ? 0 : body['from_product'];
                body['from_quantity'] = isEmpty(body['from_quantity']) ? 0 : body['from_quantity'];
                body['to_product'] = isEmpty(body['to_product']) ? 0 : body['to_product'];
                body['to_quantity'] = isEmpty(body['to_quantity']) ? 0 : body['to_quantity'];
                body['to_product_price'] = isEmpty(body['to_product_price']) ? 0 : body['to_product_price'];
                body['remark'] = isEmpty(body['remark']) ? null : body['remark'];
                // Only meaningful for INSERT (Backend rejects UPDATE of a
                // confirmed document outright regardless) -- harmless to
                // send either way.
                body['idempotency_key'] = singleIdempotencyKey;

                setSaving(true);
                try {
                    let res = await api_separate(operation, body, key_id);
                    // insert() (single) has returned the full confirmed document
                    // (with its authoritative snapshot values), not a bare 1,
                    // since the Phase 5 Backend hardening -- check for a real
                    // document id instead of the old bare-scalar contract.
                    if (res && res.id > 0) {
                        setOpen(false);
                        const docLabel = res.document_no ? ` (${res.document_no})` : '';
                        Modal.success({
                            ...getConfigModal('สำเร็จ', `${operation === 'UPDATE' ? 'แก้ไขรายการแยกแพ็คสำเร็จ' : 'บันทึกรายการแยกแพ็คสำเร็จ'}${docLabel}`),
                            ...{ onOk() { init(); }}
                        });
                    } else {
                        Modal.error({
                            ...getConfigModal('ผิดพลาด', operation === 'UPDATE' ? 'แก้ไขข้อมูลไม่สำเร็จ' : 'บันทึกรายการไม่สำเร็จ กรุณาลองใหม่อีกครั้ง'),
                        });
                    }
                } catch (err) {
                    Modal.error({
                        ...getConfigModal('ผิดพลาด', getSaveErrorMessage(err)),
                    });
                } finally {
                    setSaving(false);
                }
            } else {
                Modal.warning({
                    ...getConfigModal('แจ้งเตือน', 'กรุณากรอกข้อมูลให้ครบถ้วน')
                });
            }
        }
    }

    const onDelete = async (key_id: number) => {
        Modal.confirm({
            ...getConfigModal('แจ้งเตือน', 'คุณต้องการลบข้อมูลนี้หรือไม่ ?'),
            ...{
                async onOk() { 
                    try {
                        let res = await api_separate('DELETE', {}, key_id);
                        if (res === 1) {
                            Modal.success({
                                ...getConfigModal('สำเร็จ', 'ลบข้อมูลสำเร็จแล้ว'),
                                ...{ onOk() { init(); }}
                            });
                        } else {
                            Modal.error({
                                ...getConfigModal('ผิดพลาด', 'ลบข้อมูลไม่สำเร็จ'),
                            });
                        }
                    } catch (err: any) {
                        // Backend answers a record that is already gone with a
                        // specific 404 and a sentence written for the user. Showing
                        // only "ลบข้อมูลไม่สำเร็จ" threw that away and left them
                        // guessing whether it was the network, their permissions, or
                        // a colleague who deleted it first.
                        Modal.error({
                            ...getConfigModal('ผิดพลาด', deleteFailureMessage(err, 'ลบข้อมูลไม่สำเร็จ')),
                        });
                    }
                }
            }
        });
    }

    const onDataDetailChange = (payload: any) => {
        setSeparate(payload)
    }

    const onDataDetailMultiChange = (payload: any) => {
        setSeparateMulti(payload)
    }

    useEffect(() => {
        init();
    }, []);

    return (
        <div className="pm-page-container">
            <div className="pm-header-row">
                <div className="pm-header-title-box">
                    <h2>
                        <FontAwesomeIcon icon="scissors" style={{ color: 'var(--pm-primary, #FF7A59)' }} />
                        แยกแพคสินค้า (Separate Pack)
                    </h2>
                    <p>ตัดสต๊อกสินค้าหน่วยใหญ่เพื่อแปลงและเพิ่มเป็นสินค้าหน่วยย่อย</p>
                </div>
            </div>

            <CommonTableList page='separate' placeholder={placeholder_txt} columns={columns} data={[...data]} filterField={filterField} onAdd={() => { onShowDetail('ADD') }} onAddMulti={() => { onShowDetail('MULTI') }} />
            <CommonDetail width={mode == 'MULTI' ? '90%' : '70%'} mode={mode} title={title} open={open} onClosed={onCloseDetail} onSaved={onSave} saving={saving}
                element={ 
                    mode == 'MULTI' ? 
                    <SeparateDetailMulti separateData={[...separateMulti]} onChange={onDataDetailMultiChange} onRemarkChange={setRemark} /> : 
                    <SeparateDetail key={`separate-detail-${openSeq}`} separateData={{...separate}} onChange={onDataDetailChange} /> 
                }
            />
        </div>
    );
}

export default Separate;